/**
 * Overview Container Component
 * Container component with business logic for overview tab
 * Handles overview data validation, loading states, and data preparation
 */

import React from 'react';
import { useOverViewStore } from '@/store/overViewStore';
import { OverviewPresenter } from './OverviewPresenter';

export interface OverviewContainerProps {
  placeName: string | null;
  sessionId: string | null;
  userId: string | null;
  isOverviewLoading?: boolean;
}

export const OverviewContainer: React.FC<OverviewContainerProps> = ({
  placeName,
  sessionId,
  userId,
  isOverviewLoading = false,
}) => {
  const { overview, festivals, placesOfInterest } = useOverViewStore();

  // Determine if overview is loading - check if we have request params but no overview data yet
  const hasOverviewRequestParams = !!(sessionId && userId && placeName);

  // Check if we have any overview data - name, description, pointers, or images
  // Exclude placeholder descriptions
  const hasValidDescription =
    overview.description &&
    overview.description.trim() !== '' &&
    !overview.description.includes(
      'This is a placeholder for a location whose details are not known'
    );

  const hasValidPointers =
    overview.pointers &&
    Array.isArray(overview.pointers) &&
    overview.pointers.length > 0 &&
    overview.pointers.some(
      pointer => Array.isArray(pointer.items) && pointer.items.length > 0
    );

  const hasValidImages =
    overview.imageUrls &&
    Array.isArray(overview.imageUrls) &&
    overview.imageUrls.length > 0;

  const hasFestivals =
    festivals && Array.isArray(festivals) && festivals.length > 0;

  const hasPlacesOfInterest =
    placesOfInterest &&
    Array.isArray(placesOfInterest) &&
    placesOfInterest.length > 0;

  const hasOverviewData =
    (overview.name && overview.name.trim() !== '') ||
    hasValidDescription ||
    hasValidPointers ||
    hasValidImages ||
    hasFestivals ||
    hasPlacesOfInterest;

  const isLoading =
    hasOverviewRequestParams && !hasOverviewData && isOverviewLoading;

  console.log('🔍 [OverviewContainer] Overview State:', {
    placeName,
    sessionId,
    userId,
    hasOverviewRequestParams,
    hasOverviewData,
    isLoading,
    isOverviewLoading,
    overviewName: overview.name,
    overviewDescription: overview.description?.substring(0, 50),
    hasValidDescription,
    hasValidPointers,
    hasValidImages,
    hasFestivals,
    hasPlacesOfInterest,
    pointersCount: overview.pointers?.length || 0,
    festivalsCount: festivals.length,
    placesOfInterestCount: placesOfInterest.length,
  });

  return (
    <OverviewPresenter
      overview={overview}
      festivals={festivals}
      placesOfInterest={placesOfInterest}
      isLoading={isLoading}
      hasOverviewRequestParams={hasOverviewRequestParams}
      placeName={placeName}
    />
  );
};
